import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';

// MUI
import { Typography } from '@mui/material';

// Services
import { getStockFilters, getStockByFacility } from '../../../services/stock.service';
import { getStations, searchBasicStations } from '../../../services/station.service';
import AuthService from '../../../services/auth.service';

// Hooks
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { useAppDispatch, useAppSelector } from '../../../redux/hook';

// Redux
import { fetchAllSettings } from '../../../redux/slices/settingsSlice';

// Utils
import { makeStockFacilityColumns, buildStockTotalsRow } from '../../../utils/columns/makeStockFacilityColumns';
import { ALL, SearchableOption } from './stockDetails.constants';

// Components
import StockFiltersBar from './StockFiltersBar';
import StockResultsPanel from './StockResultsPanel';

// Types
import { StockFilterGroup, StockFilterEntry, StockFacilityResult } from '../../../types/Stock';
import { StationResponse, BasicStation } from '../../../types/Station';
import { FilterableTypes } from '../../../types/FilterableTypes';

// Constants
import { StockDetailsContent } from '../../../constants/Constants';

// Common Components
import { useNewTable } from '../../common/table/index';
import SearchBar from '../../common/SearchBar/SearchBar';

export default function StockDetails(): JSX.Element {
  const { notifyMessage } = useNotifyMessage();

  const dispatch = useAppDispatch();

  const settingStates = useAppSelector((state) => state.settings);

  const isExternalUser = AuthService.getStoredUserProfile()?.isInternal === false;

  const notifyRef = useRef(notifyMessage);

  useEffect(() => {
    dispatch(fetchAllSettings());
  }, [dispatch]);

  useEffect(() => {
    notifyRef.current = notifyMessage;
  }, [notifyMessage]);

  const [filterGroups, setFilterGroups] = useState<StockFilterGroup[]>([]);
  const [carrierId, setCarrierId] = useState<string>(ALL);
  const [equipmentType, setEquipmentType] = useState<string | undefined>();
  const [uldCategory, setUldCategory] = useState<string | undefined>();
  const [uldGroupId, setUldGroupId] = useState<string>(ALL);

  const [stations, setStations] = useState<StationResponse[]>([]);
  const [stationId, setStationId] = useState<string>('');

  const [stationInputValue, setStationInputValue] = useState<string>('');
  const [stationSearchResults, setStationSearchResults] = useState<BasicStation[]>([]);
  const [stationSearching, setStationSearching] = useState<boolean>(false);
  const [stationsLoaded, setStationsLoaded] = useState<boolean>(false);
  const [filtersLoaded, setFiltersLoaded] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    const loadStations = async (): Promise<void> => {
      try {
        const res = await getStations({
          skip: 0,
          take: 100,
        });

        const results = res.data?.results ?? [];

        if (cancelled) {
          return;
        }

        setStations(results);

        // Default to LHR if present, otherwise fall back to the first station
        // so the table still has something to load.
        const defaultStation = results.find((station) => station.iataCode === 'LHR') ?? results[0];

        if (defaultStation) {
          setStationId((previousStationId) => {
            return previousStationId || defaultStation.id;
          });
          setStationInputValue(defaultStation.iataCode);
        }
      } catch (error) {
        if (!cancelled) {
          notifyRef.current(StockDetailsContent.stationsLoadError, 'error');
        }
      } finally {
        if (!cancelled) {
          setStationsLoaded(true);
        }
      }
    };

    void loadStations();

    return () => {
      cancelled = true;
    };
  }, []);

  // DEBOUNCED STATION SEARCH
  useEffect(() => {
    const query = stationInputValue.trim();

    if (!query) {
      setStationSearchResults([]);
      setStationSearching(false);
      return;
    }

    let cancelled = false;

    const debounceTimer = window.setTimeout(async () => {
      try {
        setStationSearching(true);

        const results = await searchBasicStations(query);

        if (cancelled) {
          return;
        }

        setStationSearchResults(results);
      } catch (error) {
        if (!cancelled) {
          setStationSearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setStationSearching(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(debounceTimer);
    };
  }, [stationInputValue]);

  // STATION OPTIONS ($-suffixed codes are internal/test stations and are never shown)
  const initialStationOptions = useMemo<SearchableOption[]>(
    () =>
      stations
        .filter((station) => !station.iataCode?.includes('$'))
        .map((station) => ({
          value: station.id,
          label: station.iataCode,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [stations],
  );

  const dynamicStationOptions = useMemo<SearchableOption[]>(
    () =>
      stationSearchResults
        .map((station) => ({
          value: station.id,
          label: station.iataCode,
        }))
        .filter((option): option is SearchableOption => Boolean(option.value) && Boolean(option.label) && !option.label.includes('$'))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [stationSearchResults],
  );

  // True only while the user is actively typing a query; reset on selection so
  // reopening the field (without typing) shows the full list again, not just the
  // narrow search results left over from the last query.
  const [stationSearchDirty, setStationSearchDirty] = useState<boolean>(false);

  const handleStationInputValueChange = (value: string): void => {
    setStationSearchDirty(true);
    setStationInputValue(value);
  };

  const searchableStationOptions = useMemo<SearchableOption[]>(() => {
    const base = stationSearchDirty && stationInputValue.trim().length > 0 ? dynamicStationOptions : initialStationOptions;

    const selectedStation =
      initialStationOptions.find((option) => option.value === stationId) ?? dynamicStationOptions.find((option) => option.value === stationId);

    const combined = selectedStation ? [selectedStation, ...base] : base;

    const seen = new Set<string>();
    return combined.filter((option) => {
      if (seen.has(option.label)) return false;
      seen.add(option.label);
      return true;
    });
  }, [stationSearchDirty, stationInputValue, dynamicStationOptions, initialStationOptions, stationId]);

  useEffect(() => {
    if (!stationId) {
      setFilterGroups([]);
      setFiltersLoaded(false);
      return;
    }

    let cancelled = false;
    setFiltersLoaded(false);
    const loadFilters = async (): Promise<void> => {
      try {
        const groups = await getStockFilters(stationId);
        if (!cancelled) {
          setFilterGroups(groups);
        }
      } catch (error) {
        if (!cancelled) {
          notifyRef.current(StockDetailsContent.filtersLoadError, 'error');
        }
      } finally {
        if (!cancelled) {
          setFiltersLoaded(true);
        }
      }
    };

    void loadFilters();

    return () => {
      cancelled = true;
    };
  }, [stationId]);

  const carrierOptions = useMemo(() => filterGroups.find((group) => group.filterName === 'networkCarrierId')?.options ?? [], [filterGroups]);
  const uldTypeOptions = useMemo(() => filterGroups.find((group) => group.filterName === 'uldGroupId')?.options ?? [], [filterGroups]);

  useEffect(() => {
    if (!isExternalUser || !carrierOptions.length) {
      return;
    }
    setCarrierId((previousCarrierId) =>
      carrierOptions.some((option) => option.value === previousCarrierId) ? previousCarrierId : carrierOptions[0].value,
    );
  }, [isExternalUser, carrierOptions]);

  const searchableCarrierOptions = useMemo<SearchableOption[]>(() => {
    const options = carrierOptions
      .map((option) => ({
        value: option.value,
        label: option.label || option.value,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return isExternalUser
      ? options
      : [
          {
            value: ALL,
            label: StockDetailsContent.allOptionLabel,
          },
          ...options,
        ];
  }, [carrierOptions, isExternalUser]);

  const columns = useMemo(() => makeStockFacilityColumns(uldTypeOptions), [uldTypeOptions]);

  const buildFilters = (
    station: string,
    carrier: string,
    selectedEquipmentType: string | undefined,
    selectedUldCategory: string | undefined,
    uldGroup: string,
  ): StockFilterEntry[] => {
    const filters: StockFilterEntry[] = [];
    if (station) {
      filters.push({
        toStationId: station,
        filterType: FilterableTypes.Equals,
      });
    }
    if (carrier !== ALL) {
      filters.push({
        networkCarrierId: carrier,
        filterType: FilterableTypes.Equals,
      });
    }
    if (selectedEquipmentType != null) {
      filters.push({
        type: Number(selectedEquipmentType),
        filterType: FilterableTypes.Equals,
      });
    }
    if (selectedUldCategory != null) {
      filters.push({
        EquipmentCategory: Number(selectedUldCategory),
        filterType: FilterableTypes.Equals,
      });
    }
    if (uldGroup !== ALL) {
      filters.push({
        uldGroupId: uldGroup,
        filterType: FilterableTypes.Equals,
      });
    }
    return filters;
  };

  const [paramFilter, setParamFilter] = useState<{
    filters: StockFilterEntry[];
  }>({
    filters: [],
  });

  useEffect(() => {
    setParamFilter({
      filters: buildFilters(stationId, carrierId, equipmentType, uldCategory, uldGroupId),
    });
  }, [stationId, carrierId, equipmentType, uldCategory, uldGroupId]);

  const noCarrierAssigned = isExternalUser && filtersLoaded && carrierOptions.length === 0;

  const table = useNewTable({
    columns,
    fetchAsync: getStockByFacility,
    params: paramFilter,
    serverSide: true,

    shouldFetch: stationsLoaded && filtersLoaded && !noCarrierAssigned,
  });

  const applyFilters = (nextCarrier: string, nextUldGroup: string): void => {
    setCarrierId(nextCarrier);
    setUldGroupId(nextUldGroup);
  };

  const applyStation = (nextStationId: string, selectedOption?: SearchableOption): void => {
    setStationId(nextStationId);
    setEquipmentType(undefined);
    setUldCategory(undefined);
    setUldGroupId(ALL);
    setStationSearchDirty(false);

    if (selectedOption) {
      setStationInputValue(selectedOption.label);
    }
  };

  const tableData = (
    table as unknown as {
      data?: StockFacilityResult[];
    }
  ).data;

  // Rows without a facilityId are unassigned stock with no real location, so they are
  // kept out of both the facility breakdown table and the map.
  const rows: StockFacilityResult[] = useMemo(() => (tableData ?? []).filter((row) => row.facilityId !== null), [tableData]);

  const totalsRow = useMemo(() => buildStockTotalsRow(rows, uldTypeOptions), [rows, uldTypeOptions]);

  const dataWithTotals = useMemo(() => (rows.length ? [...rows, totalsRow] : rows), [rows, totalsRow]);

  const tableLoading =
    (
      table as unknown as {
        loading?: boolean;
      }
    ).loading ?? false;

  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);

  useEffect(() => {
    setSelectedRowIndex(null);
  }, [stationId, carrierId, equipmentType, uldCategory, uldGroupId]);

  return (
    <React.Fragment>
      <Typography
        variant='h6'
        sx={{
          m: 0,
          mb: 1.5,
          ml: 1,
          fontWeight: 700,
          color: '#1f2d63',
        }}
      >
        {StockDetailsContent.heading}
      </Typography>
      <SearchBar
        pageKey={StockDetailsContent.pageKey}
        onSearch={() => applyFilters(carrierId, uldGroupId)}
        onReset={() => applyFilters(ALL, ALL)}
        allowSearchAll={false}
        columns={columns}
        hideSearchInput
        components={
          <StockFiltersBar
            carrierId={carrierId}
            carrierOptions={searchableCarrierOptions}
            isExternalUser={isExternalUser}
            noCarrierAssigned={noCarrierAssigned}
            stationId={stationId}
            stationInputValue={stationInputValue}
            stationOptions={searchableStationOptions}
            stationSearching={stationSearching}
            onStationInputValueChange={handleStationInputValueChange}
            onStationSelect={applyStation}
            equipmentType={equipmentType}
            equipmentTypeOptions={settingStates.equipmentTypes?.value}
            onEquipmentTypeChange={setEquipmentType}
            uldCategory={uldCategory}
            uldCategoryOptions={settingStates.equipmentCategories?.value}
            onUldCategoryChange={setUldCategory}
            uldGroupId={uldGroupId}
            uldTypeOptions={uldTypeOptions}
            onApplyFilters={applyFilters}
          />
        }
      />
      <StockResultsPanel
        rows={rows}
        dataWithTotals={dataWithTotals}
        table={table}
        tableLoading={tableLoading}
        selectedRowIndex={selectedRowIndex}
        onSelectedRowIndexChange={setSelectedRowIndex}
      />
    </React.Fragment>
  );
}
